import { computed, inject, onScopeDispose, ref, watch, watchEffect, } from 'vue';
import { createExternalExampleResolver } from '../helpers/external-examples.js';
/** Supply the current document's resolver so consumers share downloads and invalidation. */
export const EXTERNAL_EXAMPLES = Symbol('external-examples');
/** Observe current visibility, rather than mounting, to avoid fetching every operation on a long page. */
export const useExampleVisibility = (target) => {
    const visible = ref(false);
    let observer;
    watch(target, (element) => {
        observer?.disconnect();
        visible.value = false;
        const node = element && ('$el' in element ? element.$el : element);
        if (typeof Element === 'undefined' || !(node instanceof Element)) {
            return;
        }
        if (typeof IntersectionObserver === 'undefined') {
            visible.value = true;
            return;
        }
        observer = new IntersectionObserver(([entry]) => {
            visible.value = entry?.isIntersecting ?? false;
        });
        observer.observe(node);
    }, { flush: 'post' });
    onScopeDispose(() => observer?.disconnect());
    return visible;
};
/** Resolve only the selected examples and expose immutable views of their downloaded values. */
export const useExternalExamples = (examples, enabled = () => true, resolver) => {
    const fallback = createExternalExampleResolver();
    const getResolver = resolver ?? inject(EXTERNAL_EXAMPLES, () => fallback);
    const states = computed(() => examples()
        .filter((example) => example !== undefined && example.value === undefined && !!example.externalValue)
        .map((example) => getResolver()(example)));
    watchEffect(() => {
        if (!enabled()) {
            return;
        }
        for (const state of states.value) {
            if (state.status === 'idle') {
                void state.load();
            }
        }
    });
    return {
        pending: computed(() => states.value.some((state) => state.status !== 'loaded')),
        failed: computed(() => states.value.some((state) => state.status === 'error')),
        resolve: (example) => {
            if (!example || example.value !== undefined || !example.externalValue) {
                return example;
            }
            const state = getResolver()(example);
            if (state.status !== 'loaded') {
                return example;
            }
            // A 3.2 example can pair structured data with external wire text. Keep both in the
            // resolved view so raw consumers display the download and form editors retain the data.
            if (example.dataValue !== undefined && state.serializedValue !== undefined) {
                return { ...example, serializedValue: state.serializedValue };
            }
            return { ...example, value: state.value };
        },
        retry: async () => {
            await Promise.all(states.value.map((state) => state.load()));
        },
    };
};
